"use client"

/**
 * NetworkDisplayAd — renders a JS embed from an ad network (ExoClick, TrafficStars etc.)
 *
 * ── Why this is an iframe at all ──────────────────────────────────────────
 * This used to inject the network's script straight into our own document.
 * That gave it full access to our page, and it was abused in practice:
 * ExoClick's ad-provider.js entered a broken internal retry loop (an uncaught
 * "Cannot read properties of null (reading 'length')" firing every setTimeout
 * tick) and clicks meant for OUR UI — including the interstitial's own close
 * button — were being intercepted before they reached it. A sandboxed iframe
 * physically cannot reach outside its own frame, so however badly a network's
 * script behaves, it stays in its box. That containment is worth keeping.
 *
 * ── Why the ads then looked cropped/broken ────────────────────────────────
 * An iframe is a fixed box; unlike an inline <div>, it does NOT grow to fit
 * its content. Every ExoClick display creative here is a fixed 300px wide
 * banner, while a grid column is ~242px. So the creative overflowed
 * horizontally, the frame grew a horizontal scrollbar, and that scrollbar ate
 * enough vertical space to produce a vertical one too. That double-scrollbar
 * is exactly the "ads are cropped / forced to square / look broken" report —
 * and note it was primarily a WIDTH problem, not the height problem it
 * appeared to be.
 *
 * ── The fix ───────────────────────────────────────────────────────────────
 * Don't try to squeeze a fixed-size creative into an arbitrary box. Render
 * the iframe at the creative's true native size (from ad_zones.creative_width
 * /creative_height — see migration 20261111000100, sourced from the network's
 * own zone dashboard), then CSS-scale the whole frame down to fit whatever
 * width the column actually has. The creative is always fully visible, always
 * correctly proportioned, and scrollbars are impossible because the frame is
 * never smaller than its content. Same scale-to-fit approach PillarboxAd
 * already uses.
 *
 * This is deterministic: it needs no measurement of the cross-origin frame
 * (which the browser forbids, regardless of sandbox flags) and no cooperation
 * from the network's script. If a zone has no configured size, we fall back to
 * the legacy full-width behavior plus the postMessage self-measurement below,
 * which still works for srcDoc embeds we author ourselves.
 */

import { useState, useEffect, useRef, useCallback } from "react"
import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { useAdStore } from "@/lib/ad-store"
import { useHasConsent } from "@/components/cookie-consent"
import { isAdCooldownExpired, isAdTestModeOn, markAdShown } from "@/lib/ad-cooldown"
import { isGlobalAdsOff } from "@/lib/global-ads-off"
import { useAdFrequencyStore } from "@/lib/ad-frequency-store"
import { extractExoclickZoneId, exoclickIframeSrc } from "@/lib/ad-embed"
import { ZoneImpressionBeacon } from "@/components/ads/ZoneImpressionBeacon"
import { useTranslations } from "next-intl"

interface NetworkDisplayAdProps {
  embedJs?: string | null   // Raw JS/HTML from the network (optional — renders nothing if missing)
  className?: string
  label?: string            // "Sponsored", "Ad", etc.
  minHeight?: number        // Fallback height when the zone has no configured size
  maxHeight?: number        // Cap so one oversized zone can't dominate the feed
  /** True native size of this zone's creative, from ad_zones.creative_width /
   *  creative_height. When present, the frame renders at exactly this size and
   *  is scaled to fit — the reliable path. */
  zoneWidth?: number | null
  zoneHeight?: number | null
  placeholderVariant?: "leaderboard" | "rectangle" | "inline" | "strip"
  /**
   * Our ad_zones UUID, for impression reporting. This is the grid placement —
   * the highest-volume one on the site — and it recorded nothing at all
   * because every caller dropped zone_id from the serve response. Optional:
   * callers that genuinely have no zone (e.g. the test harness passing a null
   * embed) simply don't report. NOT ExoClick's numeric id.
   */
  zoneId?: string | null
}

// Marker key so the listener only reacts to our own measuring script.
const HEIGHT_MESSAGE_KEY = "__goonruAdHeight"

export function NetworkDisplayAd({
  embedJs,
  className,
  label = "Sponsored",
  minHeight = 250,
  maxHeight = 600,
  zoneWidth,
  zoneHeight,
  zoneId,
}: NetworkDisplayAdProps) {
  const t = useTranslations("networkDisplayAd")
  const hasConsent = useHasConsent()
  const { isPremiumUser } = useAdStore()
  const [canShow] = useState(() =>
    isAdCooldownExpired("inline_grid", useAdFrequencyStore.getState().getCooldownMinutes("inline_sponsored_ads"))
  )
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null)
  const [availableWidth, setAvailableWidth] = useState<number | null>(null)

  // Track the column's real width so the creative can be scaled to fit it.
  // A callback ref (not useRef + effect) so this attaches correctly no matter
  // the mount order, and re-measures on every resize/column-count change.
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const setWrapperRef = useCallback((node: HTMLDivElement | null) => {
    wrapperRef.current = node
    if (node) setAvailableWidth(node.clientWidth)
  }, [])

  useEffect(() => {
    const el = wrapperRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const ro = new ResizeObserver(() => setAvailableWidth(el.clientWidth))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  useEffect(() => {
    if (canShow) markAdShown("inline_grid")
  }, [canShow])

  // Self-measurement fallback for srcDoc embeds we author (see measureScript
  // below). Only used when the zone has no configured native size.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return
      const height = event.data?.[HEIGHT_MESSAGE_KEY]
      if (typeof height === "number" && Number.isFinite(height) && height > 0) {
        setMeasuredHeight(Math.min(Math.max(height, minHeight), maxHeight))
      }
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [minHeight, maxHeight])

  // Premium users never see ads or placeholders — except during a
  // deliberate admin self-test (Force Ads test mode). Without the
  // isAdTestModeOn() escape hatch this component is permanently broken for
  // its only realistic tester: admin/super_admin accounts are always
  // treated as premium (see hasPremiumAccess() sync in lib/auth-store.ts), so an
  // admin flipping Force Ads on and browsing a normal grid page would still
  // see every single network_ad slot render nothing — not because the fix
  // to booru-grid.tsx's own injection gating was wrong, but because this
  // component re-checks the same raw isPremiumUser independently and had no
  // matching override. Same reasoning as effectivePremiumUser in
  // booru-grid.tsx: a genuinely premium real visitor still never sees ads,
  // this only carves out the explicit self-test.
  if ((isPremiumUser || isGlobalAdsOff()) && !isAdTestModeOn()) return null
  // No consent — show nothing
  if (!hasConsent) return null
  // No embed configured yet
  if (!embedJs) return null
  if (!canShow) return null

  // A recognized ExoClick zone tag gets a real cross-origin iframe (its own
  // origin, so its cookie writes succeed and the creative actually renders —
  // see lib/ad-embed.ts). Anything else is wrapped in srcDoc.
  // ExoClick's NUMERIC id from the embed tag. Renamed so it no longer
  // shadows the ad_zones UUID prop `zoneId` used for impression reporting —
  // feeding the UUID to exoclickIframeSrc() would break the frame entirely.
  const exoZoneId = extractExoclickZoneId(embedJs)

  const measureScript = `<script>(function(){function report(){var h=document.body.scrollHeight;if(h>0)window.parent.postMessage({${JSON.stringify(HEIGHT_MESSAGE_KEY)}:h},'*')}try{new ResizeObserver(report).observe(document.body)}catch(e){}window.addEventListener('load',report);setTimeout(report,500);setTimeout(report,1500)})();</script>`

  const srcDoc =
    `<!DOCTYPE html><html><head><style>html,body{margin:0;padding:0;overflow:hidden;background:transparent}</style></head><body>${embedJs}${measureScript}</body></html>`

  // ── Scale-to-fit path (used whenever the zone's true size is known) ──
  const hasNativeSize = !!zoneWidth && !!zoneHeight && zoneWidth > 0 && zoneHeight > 0

  if (hasNativeSize) {
    const nativeW = zoneWidth as number
    const nativeH = zoneHeight as number
    // Never upscale past 1:1 — a 300px creative stretched to a 500px column
    // would just look blurry. It centers in the leftover room instead.
    const scale = availableWidth ? Math.min(1, availableWidth / nativeW) : 1
    const scaledW = nativeW * scale
    const scaledH = nativeH * scale
    const offsetX = availableWidth ? Math.max(0, (availableWidth - scaledW) / 2) : 0

    // The creative's document is reliably a few px LARGER than the creative
    // itself, because the ad's own <body> keeps a default margin that we
    // cannot reset from out here (cross-origin). Measured live by reading
    // inside the real frames: a 300x500 zone's document scrolls to 300x504,
    // a 300x250 zone's to 300x254 — consistently ~4px. An iframe sized to
    // exactly nativeH therefore overflows by those few pixels and can grow
    // a scrollbar even when the declared zone size is perfectly correct,
    // which is its own separate cause of the reported scrollbars (distinct
    // from a genuinely wrong creative_height).
    //
    // So give the FRAME a small vertical buffer while leaving the WRAPPER at
    // the true scaled height: the frame is then never smaller than its own
    // document, and the wrapper's overflow:hidden clips the buffer — which
    // holds nothing but that dead margin, so nothing real is lost.
    const OVERFLOW_BUFFER_PX = 16
    const frameH = nativeH + OVERFLOW_BUFFER_PX

    return (
      <div
        ref={setWrapperRef}
        className={cn(
          "relative overflow-hidden rounded-lg border border-border/30 bg-muted/20",
          className,
        )}
        // Height follows the scaled creative exactly, so the grid reserves the
        // right amount of space and nothing is clipped or left as dead area.
        style={{ height: Math.round(scaledH) }}
      >
        <ZoneImpressionBeacon zoneId={zoneId} />
        <Badge
          variant="outline"
          className="absolute top-1.5 left-1.5 z-10 text-[9px] px-1 py-0 text-muted-foreground border-muted-foreground/30"
        >
          {label}
        </Badge>
        <iframe
          ref={iframeRef}
          src={exoZoneId ? exoclickIframeSrc(exoZoneId) : undefined}
          srcDoc={exoZoneId ? undefined : srcDoc}
          sandbox={exoZoneId ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" : "allow-scripts"}
          title={t("advertisement")}
          scrolling="no"
          width={nativeW}
          height={frameH}
          style={{
            // Rendered at TRUE native size (plus the margin buffer above),
            // then scaled — so the frame is never smaller than its content
            // and scrollbars can't appear.
            width: nativeW,
            height: frameH,
            border: 0,
            display: "block",
            position: "absolute",
            top: 0,
            left: offsetX,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        />
      </div>
    )
  }

  // ── Legacy path: zone size unknown. Full-width frame, height from the
  // srcDoc self-measurement when available, else the minHeight fallback. ──
  const effectiveHeight = measuredHeight ?? minHeight

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border border-border/30 bg-muted/20",
        className,
      )}
      style={{ minHeight, height: effectiveHeight }}
    >
      <ZoneImpressionBeacon zoneId={zoneId} />
      <Badge
        variant="outline"
        className="absolute top-1.5 left-1.5 z-10 text-[9px] px-1 py-0 text-muted-foreground border-muted-foreground/30"
      >
        {label}
      </Badge>
      <iframe
        ref={iframeRef}
        src={exoZoneId ? exoclickIframeSrc(exoZoneId) : undefined}
        srcDoc={exoZoneId ? undefined : srcDoc}
        sandbox={exoZoneId ? "allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox" : "allow-scripts"}
        title={t("advertisement")}
        scrolling="no"
        style={{ width: "100%", height: effectiveHeight, border: 0, display: "block" }}
      />
    </div>
  )
}
